import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { InputObservable } from '@y42/angular/util';
import { ListViewColDef, provideListView } from '@y42/core/list-view';
import { ExportListViewData } from '@y42/data-export';
import { DatasourceListViewColumnDefsService } from '@y42/datasource/shared/list-view';
import { getDatasourceListFrameworkComponents, getDatasourceListViewGridOptions } from '@y42/feature/shared/list-view';
import { UserQuery } from '@y42/user/state';
import { GridApi, GridOptions, IRowNode, SelectionChangedEvent } from 'ag-grid-community';
import merge from 'lodash/merge';
import { combineLatest, debounceTime, map, Observable, of, startWith, Subject, switchMap, tap } from 'rxjs';
import { OrchestrationExportSelectorService } from './orchestration-export-selector.service';

@UntilDestroy()
@Component({
  selector: 'y42-orchestration-export-selector',
  templateUrl: './orchestration-export-selector.component.html',
  styleUrls: ['./orchestration-export-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [provideListView(OrchestrationExportSelectorService)],
})
export class OrchestrationExportSelectorComponent {
  constructor(
    private readonly userQuery: UserQuery,
    private readonly datasourceListViewColumnDefsService: DatasourceListViewColumnDefsService,
  ) {
    this.setCheckboxSelectionsOnSelectedIdsChange().subscribe();
  }

  @Input()
  @InputObservable()
  selectedIds?: Array<ExportListViewData['$path']>;
  private readonly selectedIds$!: Observable<Array<ExportListViewData['$path']> | undefined>;

  @Output() exportsSelectionChanged = new EventEmitter<string[]>();

  gridApi?: GridApi;
  private readonly gridReady$ = new Subject<void>();

  readonly frameworkComponents: GridOptions['components'] = (() => {
    const components = getDatasourceListFrameworkComponents() || {};
    delete components.jobStatus;
    return components;
  })();

  readonly columnDefs: ListViewColDef[] = Object.values(
    merge(
      this.datasourceListViewColumnDefsService.columnDefs({
        locale: this.userQuery.getActiveUser()?.locale,
        disableStarring: true,
        disableTagging: true,
      }),
      {
        child_name: {
          headerName: 'Triggers',
        },
      } as Record<keyof ExportListViewData, Partial<ListViewColDef>>,
    ),
  );

  gridOptions: GridOptions = this.getGridOptions();

  gridReady({ api }: { api?: GridApi | null }) {
    this.gridApi = api!;
    this.gridReady$.next();
  }

  selectExports(event: SelectionChangedEvent) {
    const api = event.api;
    const data: ExportListViewData[] = api.getSelectedNodes().map((node) => node.data);
    this.exportsSelectionChanged.emit(data.map((t) => t.child_$path!));
  }

  private getGridOptions() {
    return getDatasourceListViewGridOptions({
      importActiveRowClassRule: (__) => false,
      hideSubEntries: false,
      selectionDisabled: false,
      rowSelection: 'multiple',
    });
  }

  private setCheckboxSelectionsOnSelectedIdsChange() {
    return combineLatest([this.gridReady$, this.selectedIds$.pipe(startWith([]))]).pipe(
      debounceTime(200),
      map(([__, selectedIds]) => selectedIds as Array<ExportListViewData['child_$path']>),
      switchMap((selectedIds) => {
        // if (this.selectionDisabled) {
        //   return of(null);
        // }

        let firstNodeToPosition: IRowNode | null = null;
        this.gridApi?.forEachNode((node) => {
          const data: ExportListViewData | undefined = node?.data;
          const childId = data?.child_$path;
          if (!node.group && childId && selectedIds?.includes(childId)) {
            if (!node.isSelected()) {
              node.setSelected(true, undefined, true);
              if (node.parent && node.parent.field && node.parent.field === 'id') {
                node.parent.setExpanded(true);
                if (!firstNodeToPosition) {
                  firstNodeToPosition = node;
                }
              }
            }
          }
        });

        return firstNodeToPosition
          ? of(firstNodeToPosition).pipe(
              debounceTime(500),
              tap((node) => this.gridApi?.ensureNodeVisible(node, 'middle')),
            )
          : of(null);
      }),
      untilDestroyed(this),
    );
  }
}
